/**
 * Owner Art Style client: the brand's own art styles, kept by the spark-creatives service under
 * `/creatives/tenant/{tenant_id}/art-styles…` (specs `.claude/specs/2026-09-30-owner-art-style.md` and
 * `.claude/specs/2026-10-01-art-style-multi.md`, cast: `.claude/specs/2026-10-01-art-style-mascots.md`).
 *
 * A brand has any number of styles; at most one is active (the one spark adds to the brand's banner and image
 * draws). Each style's inputs (mood board, direction, mascots, the brand logo snapshot) are autosaved with a
 * partial PUT guarded by `expected_revision`; extraction, undo, samples and activation are explicit actions
 * that read the SAVED record. Extractions and sample draws are async jobs on the shared creative-jobs machinery
 * (kinds `art_style_extract` and `art_style_sample`, the style id in `root_banner_id`).
 *
 * Every call goes through the shared spark-creatives request config (`creativesRequestConfig`), so a failure
 * from this separate service never rotates the owner-hub token, and every status is parsed into an
 * {@link ArtStyleResult} instead of throwing.
 */
import axios from 'axios';

import {
  creativesRequestConfig,
  creativesServiceBase,
  creativesServiceConfigured,
  slotAspect,
  slotOfVariant,
} from '../creatives/creative-api';

/* ── record ─────────────────────────────────────────────────────────────────── */

/** The four sample slots, each a different format of the same style. */
export type ArtStyleSlot = 'hero' | 'promo' | 'story' | 'reward';

/** The sample slots in display order. */
export const ART_STYLE_SLOTS: readonly ArtStyleSlot[] = ['hero', 'promo', 'story', 'reward'];

/**
 * How the environment extracts, previews and saves styles (the record's `pipeline`, the same for every style there):
 * - `casino_creation`: casino-creation's shared extraction writes the canonical `art_style`, and the samples are ONE
 *   batch of four draft previews of its evergreens (`previews`), each followed by its own `art_style_sample` job
 *   (placement `preview_<n>`, `parent_id` = the batch id);
 * - `local`: spark-creatives' own extraction and the per-slot `samples`, as before.
 * A service from before the pipelines sends none: that is `local`.
 */
export type ArtStylePipeline = 'casino_creation' | 'local';

/** Previews in one batch (casino-creation always draws four). */
export const ART_STYLE_PREVIEW_COUNT = 4;

/** The placement of a preview batch's n-th follower job (n = 1..{@link ART_STYLE_PREVIEW_COUNT}). */
export const previewPlacement = (n: number): string => `preview_${n}`;

/** Which preview of its batch a follower job follows (1..{@link ART_STYLE_PREVIEW_COUNT}), from its placement;
 *  undefined for any other placement. Pure. */
export const previewNumberOf = (placement: string | undefined): number | undefined => {
  const match = /^preview_(\d+)$/.exec(placement ?? '');
  const n = match ? Number(match[1]) : NaN;
  return Number.isInteger(n) && n >= 1 && n <= ART_STYLE_PREVIEW_COUNT ? n : undefined;
};

/** Caps the service validates (mirrored here so the UI never offers more). */
export const MAX_MOOD_IMAGES = 4;
/** Reference images per mascot (all of the SAME character). */
export const MAX_MASCOT_IMAGES = 4;
/** Mascots in one style's cast. */
export const MAX_MASCOTS = 10;
export const MAX_PROMPT_CHARS = 1000;

/** Which list an uploaded reference image is meant for. */
export type ArtStyleImageKind = 'mood' | 'mascot';

/** A stored reference image. */
export interface ArtStyleImage {
  url: string;
  name: string;
  /** Read-only, mood images on the casino_creation pipeline: the URL casino-creation is sent for it (its text-free
   *  copy when one exists, else `url`). "" or absent elsewhere; never sent back (see {@link artStylePatchBody}). */
  used_url?: string;
}

/** One named character of a style's cast (spec `.claude/specs/2026-10-01-art-style-mascots.md`). Its avatar is
 *  a round crop of its first image. `brief` is Lumio's illustrator's brief of the character: written by
 *  `describe`, dropped by the service when the images change, never sent by the client. */
export interface ArtStyleMascot {
  id: string;
  name: string;
  brief: string;
  images: ArtStyleImage[];
  created_at: string;
}

/** Which mascot a generation draws (sent as `mascot_id` on `/creatives/generate`): {@link MASCOT_AUTO} rotates
 *  across the active style's cast, {@link MASCOT_NONE} draws none, any other value is one mascot's id (an id the
 *  cast no longer has is treated as auto by the service). */
export type MascotPick = string;
export const MASCOT_AUTO: MascotPick = '';
export const MASCOT_NONE: MascotPick = 'none';

/** One palette colour of an extracted style. */
export interface ArtStyleSwatch {
  name: string;
  hex: string;
  role: string;
}

/** What one casino-creation extraction was sent, as sent ("" for an input it was not sent). */
export interface ArtStyleExtractionReferences {
  /** The mood images in order (each one's text-free copy where one existed, else the original). */
  images: string[];
  logo_url: string;
  direction: string;
  behavior: string;
}

/** When and from what the current definition was extracted. */
export interface ArtStyleExtraction {
  model: string;
  at: string;
  /** Signature of the inputs the extraction read; differs from the record's `inputs_sig` once they change. */
  inputs_sig: string;
  /** casino-creation's provenance of the extraction ("" / {} for a local one). */
  prompt_version: string;
  prompt_revisions: Record<string, number>;
  provider_response_id: string;
  /** What a casino-creation extraction was sent; null for a local one. */
  references: ArtStyleExtractionReferences | null;
}

/** The one-step undo slot: the definition an extraction (or an undo) replaced. */
export interface ArtStylePrevious {
  definition: string;
  palette: ArtStyleSwatch[];
  avoid: string;
  /** The canonical style text it replaced ("" when that was a local extraction). */
  art_style: string;
}

/**
 * A preview batch's state: `new` (charged and queued, not sent yet), `posting` (being sent to casino-creation),
 * `posted` (casino-creation took it, each preview follows its own state), `refused` (casino-creation refused it, all
 * four refunded) or `uncertain` (whether casino-creation took it is unknown: all four refunded, the previews may still
 * be drawing, it is never sent again, and a new batch may be asked for).
 */
export type ArtStylePreviewsState = 'new' | 'posting' | 'posted' | 'refused' | 'uncertain';

const PREVIEWS_STATES: readonly ArtStylePreviewsState[] = ['new', 'posting', 'posted', 'refused', 'uncertain'];

/** One preview's state; every preview of a refused or uncertain batch is `failed`, `discarded` = its job was stopped. */
export type ArtStylePreviewItemState = 'queued' | 'generating' | 'ready' | 'failed' | 'discarded';

const PREVIEW_ITEM_STATES: readonly ArtStylePreviewItemState[] = ['queued', 'generating', 'ready', 'failed', 'discarded'];

/** One preview of a batch, followed by its own `art_style_sample` job (placement `preview_<n>`). */
export interface ArtStylePreviewItem {
  /** 1..{@link ART_STYLE_PREVIEW_COUNT}. */
  n: number;
  job_id: string;
  /** The casino-creation evergreen it is drawn for ("" until the batch is posted). */
  evergreen_name: string;
  /** `evergreen_name` made readable by the service ("" until the batch is posted). */
  label: string;
  /** The renderer casino-creation draws it with (`seedream_direct` for a mature style); "" until known. */
  effective_renderer: string;
  state: ArtStylePreviewItemState;
  /** The drawn preview; "" until ready. */
  candidate_url: string;
  error: string;
}

/** A style's newest preview batch (the casino_creation pipeline's samples). */
export interface ArtStylePreviews {
  batch_id: string;
  state: ArtStylePreviewsState;
  preview_sig: string;
  /** The signature a batch asked for now would carry ("" when the style cannot preview now). */
  current_sig: string;
  /** The style, its references, or the renderer or format changed since the batch was asked for. */
  outdated: boolean;
  /** The renderer the batch asked for. */
  renderer: string;
  /** The one format every preview is drawn in: `square_ar11_keycenter` (1:1) or `landscape_ar21_keycenter` (2:1). */
  field_name: string;
  /** The reference image URLs the batch sent, in order (not shown: the ingredients rail already shows them). */
  references: string[];
  created_at: string;
  /** One line for a refused or uncertain batch; "" otherwise. */
  error: string;
  /** In n order. */
  items: ArtStylePreviewItem[];
}

/** The 2:1 preview format; anything else is drawn square. */
export const PREVIEW_FIELD_LANDSCAPE = 'landscape_ar21_keycenter';

/** One drawn sample. `style_sig` is the style it was drawn with (stale once the style changes). */
export interface ArtStyleSample {
  url: string;
  job_id: string;
  created_at: string;
  style_sig: string;
}

export type ArtStyleStatus = 'draft' | 'active';

/** Who set the "mature" flag: nobody yet (`''`), the extraction (it read the mood board and mascot), a draw
 *  gpt-image refused on moderation grounds, or the owner (then neither of the others changes it). */
export type ArtStyleMatureSource = '' | 'extraction' | 'moderation' | 'owner';

const MATURE_SOURCES: readonly ArtStyleMatureSource[] = ['', 'extraction', 'moderation', 'owner'];

/** One of the brand's art styles. */
export interface ArtStyle {
  id: string;
  tenant_id: string;
  name: string;
  status: ArtStyleStatus;
  /** How this environment extracts and previews (see {@link ArtStylePipeline}); the page branches on it. */
  pipeline: ArtStylePipeline;
  logo_url: string;
  mood_images: ArtStyleImage[];
  prompt: string;
  /** The style's cast, at most {@link MAX_MASCOTS}. */
  mascots: ArtStyleMascot[];
  definition: string;
  palette: ArtStyleSwatch[];
  avoid: string;
  /** Read-only: the canonical style text casino-creation's extraction wrote. "" for a style never extracted on the
   *  casino_creation pipeline (it extracts again before it can preview there). */
  art_style: string;
  /** Stored, but no longer read: the mood board is always used when it has images (there is no owner switch). */
  mood_enabled: boolean;
  /** Read-only, false: no owner control. */
  dynamic_mascots: boolean;
  /** Read-only: the style has an `art_style` but no maturity rating, so previews and the site save are refused until
   *  an extraction rates it. Always false on the local pipeline. */
  rating_needed: boolean;
  /** The newest preview batch (casino_creation pipeline); null when the style has none. */
  previews: ArtStylePreviews | null;
  /** Legacy (written by extractions before the cast); per-mascot briefs replace it. */
  character_description: string;
  extraction: ArtStyleExtraction | null;
  inputs_sig: string;
  previous: ArtStylePrevious | null;
  style_sig: string;
  /** Per slot, newest first. */
  samples: Record<ArtStyleSlot, ArtStyleSample[]>;
  revision: number;
  site_regen_available: boolean;
  /** Adult-oriented: while this style is the active one, the brand's draws go straight to Seedream (spec
   *  `.claude/specs/2026-10-01-art-style-mature.md`). */
  mature: boolean;
  mature_source: ArtStyleMatureSource;
  updated_at: string;
}

/** A style as the list shows it (`GET art-styles`). `thumb_url` is the newest hero sample, else the newest
 *  sample, else the first ready preview, else the first mood image, else "". */
export interface ArtStyleSummary {
  id: string;
  name: string;
  status: ArtStyleStatus;
  thumb_url: string;
  has_definition: boolean;
  mood_count: number;
  mascot_count: number;
  mature: boolean;
  created_at: string;
  updated_at: string;
}

/** The fields a PUT may change (omitted = unchanged). */
export interface ArtStylePatch {
  name?: string;
  logo_url?: string;
  mood_images?: ArtStyleImage[];
  prompt?: string;
  /** The WHOLE cast (like `mood_images`). Held as full records so the page can show an unsaved list; only
   *  `id`, `name` and `images` are sent (see {@link saveArtStyle}). */
  mascots?: ArtStyleMascot[];
  definition?: string;
  palette?: ArtStyleSwatch[];
  avoid?: string;
  /** The owner's call (the service records it with source `owner`). */
  mature?: boolean;
}

/** What a sample draw queued: one job per slot (a {@link ArtStyleSlot}, or `preview_<n>` for a preview batch's
 *  followers), the credits it charged, and on the casino_creation pipeline the batch the jobs follow ("" otherwise). */
export interface ArtStyleSampleRun {
  jobs: { slot: string; job_id: string }[];
  cost: number;
  batch_id: string;
}

/** The extraction job an extract request answered with: the one it queued, or the one already queued or running
 *  for the style (no second one starts). */
export interface ArtStyleExtractRun {
  job_id: string;
  status: 'queued' | 'running';
}

/** The placement of an extraction job (its kind is `ART_STYLE_EXTRACT_KIND`, the style id in `root_banner_id`). */
export const ART_STYLE_EXTRACT_PLACEMENT = 'extract';

/* ── results ────────────────────────────────────────────────────────────────── */

/**
 * Why a call produced no value:
 * - `unavailable`: no creatives service configured here, or the route is not deployed (404);
 * - `stale`: a PUT's `expected_revision` no longer matches (409 `stale_revision`);
 * - `insufficient`: not enough credits (402), with the shortfall when the service reports it;
 * - `refused`: the service declined with a reason code (`message`), e.g. `site_regen_unavailable`;
 * - `failed`: anything else (network, 5xx, an unexpected body).
 */
export type ArtStyleFailure = {
  ok: false;
  reason: 'unavailable' | 'stale' | 'insufficient' | 'refused' | 'failed';
  status?: number;
  message?: string;
  needed?: number;
  balance?: number;
};

export type ArtStyleResult<T> = { ok: true; value: T } | ArtStyleFailure;

/* ── parsing (tolerant: a missing field falls back to its default) ─────────── */

type Json = Record<string, unknown>;

const asObject = (value: unknown): Json => (value && typeof value === 'object' ? (value as Json) : {});
const asString = (value: unknown): string => (typeof value === 'string' ? value : '');
const asNumber = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined;

const parseImages = (value: unknown): ArtStyleImage[] =>
  Array.isArray(value)
    ? value
        .map((item) => asObject(item))
        .filter((item) => asString(item.url) !== '')
        .map((item) => {
          const usedUrl = asString(item.used_url);
          return { url: asString(item.url), name: asString(item.name), ...(usedUrl ? { used_url: usedUrl } : {}) };
        })
    : [];

const asStrings = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string' && item !== '') : [];

const oneOf = <T extends string>(allowed: readonly T[], value: unknown, fallback: T): T =>
  allowed.find((item) => item === value) ?? fallback;

const parseExtraction = (value: unknown): ArtStyleExtraction | null => {
  if (!value || typeof value !== 'object') return null;
  const data = asObject(value);
  const revisions = asObject(data.prompt_revisions);
  const references = data.references && typeof data.references === 'object' ? asObject(data.references) : null;
  return {
    model: asString(data.model),
    at: asString(data.at),
    inputs_sig: asString(data.inputs_sig),
    prompt_version: asString(data.prompt_version),
    prompt_revisions: Object.fromEntries(
      Object.entries(revisions).filter((entry): entry is [string, number] => asNumber(entry[1]) !== undefined),
    ),
    provider_response_id: asString(data.provider_response_id),
    references: references
      ? {
          images: asStrings(references.images),
          logo_url: asString(references.logo_url),
          direction: asString(references.direction),
          behavior: asString(references.behavior),
        }
      : null,
  };
};

/** One preview of a batch. A state this client does not know reads as failed (the service maps the same way). */
const parsePreviewItem = (value: unknown, index: number): ArtStylePreviewItem => {
  const data = asObject(value);
  const n = asNumber(data.n);
  return {
    n: n !== undefined && n >= 1 ? n : index + 1,
    job_id: asString(data.job_id),
    evergreen_name: asString(data.evergreen_name),
    label: asString(data.label),
    effective_renderer: asString(data.effective_renderer),
    state: oneOf(PREVIEW_ITEM_STATES, data.state, 'failed'),
    candidate_url: asString(data.candidate_url),
    error: asString(data.error),
  };
};

/** The record's `previews` (null when absent: no batch yet, or a service from before them). A batch state this client
 *  does not know reads as uncertain: whether it draws is not known, and a new batch may be asked for. Pure. */
export const parsePreviews = (value: unknown): ArtStylePreviews | null => {
  if (!value || typeof value !== 'object') return null;
  const data = asObject(value);
  const batchId = asString(data.batch_id);
  if (!batchId) return null;
  return {
    batch_id: batchId,
    state: oneOf(PREVIEWS_STATES, data.state, 'uncertain'),
    preview_sig: asString(data.preview_sig),
    current_sig: asString(data.current_sig),
    outdated: data.outdated === true,
    renderer: asString(data.renderer),
    field_name: asString(data.field_name),
    references: asStrings(data.references),
    created_at: asString(data.created_at),
    error: asString(data.error),
    items: Array.isArray(data.items) ? data.items.map(parsePreviewItem) : [],
  };
};

const parsePalette = (value: unknown): ArtStyleSwatch[] =>
  Array.isArray(value)
    ? value
        .map((item) => asObject(item))
        .filter((item) => asString(item.hex) !== '')
        .map((item) => ({ name: asString(item.name), hex: asString(item.hex), role: asString(item.role) }))
    : [];

const parseSamples = (value: unknown): ArtStyleSample[] =>
  Array.isArray(value)
    ? value
        .map((item) => asObject(item))
        .filter((item) => asString(item.url) !== '')
        .map((item) => ({
          url: asString(item.url),
          job_id: asString(item.job_id),
          created_at: asString(item.created_at),
          style_sig: asString(item.style_sig),
        }))
    : [];

const parseMascots = (value: unknown): ArtStyleMascot[] =>
  Array.isArray(value)
    ? value
        .map((item) => asObject(item))
        .filter((item) => asString(item.id) !== '')
        .map((item) => ({
          id: asString(item.id),
          name: asString(item.name),
          brief: asString(item.brief),
          images: parseImages(item.images),
          created_at: asString(item.created_at),
        }))
    : [];

/** The cast of a record. A service from before the cast sends `mascot_images` instead (photos that could be
 *  different characters): each becomes its own unnamed mascot, with an id stable across reads. */
const castOf = (data: Json): ArtStyleMascot[] =>
  Array.isArray(data.mascots)
    ? parseMascots(data.mascots)
    : parseImages(data.mascot_images).map((image, index) => ({
        id: `legacy-${index}`,
        name: '',
        brief: '',
        images: [image],
        created_at: '',
      }));

/** Map a response body onto an {@link ArtStyle}. Pure. */
export const parseArtStyle = (body: unknown): ArtStyle => {
  const data = asObject(body);
  const previous = data.previous && typeof data.previous === 'object' ? asObject(data.previous) : null;
  const samples = asObject(data.samples);
  return {
    id: asString(data.id),
    tenant_id: String(data.tenant_id ?? ''),
    name: asString(data.name),
    status: data.status === 'active' ? 'active' : 'draft',
    pipeline: data.pipeline === 'casino_creation' ? 'casino_creation' : 'local',
    logo_url: asString(data.logo_url),
    mood_images: parseImages(data.mood_images),
    prompt: asString(data.prompt),
    mascots: castOf(data),
    definition: asString(data.definition),
    palette: parsePalette(data.palette),
    avoid: asString(data.avoid),
    art_style: asString(data.art_style),
    // On unless the service says it is off (a service from before the switch drew from the images it had).
    mood_enabled: data.mood_enabled !== false,
    dynamic_mascots: data.dynamic_mascots === true,
    rating_needed: data.rating_needed === true,
    previews: parsePreviews(data.previews),
    character_description: asString(data.character_description),
    extraction: parseExtraction(data.extraction),
    inputs_sig: asString(data.inputs_sig),
    previous: previous
      ? {
          definition: asString(previous.definition),
          palette: parsePalette(previous.palette),
          avoid: asString(previous.avoid),
          art_style: asString(previous.art_style),
        }
      : null,
    style_sig: asString(data.style_sig),
    samples: {
      hero: parseSamples(samples.hero),
      promo: parseSamples(samples.promo),
      story: parseSamples(samples.story),
      reward: parseSamples(samples.reward),
    },
    revision: asNumber(data.revision) ?? 0,
    site_regen_available: data.site_regen_available === true,
    mature: data.mature === true,
    mature_source: MATURE_SOURCES.find((source) => source === data.mature_source) ?? '',
    updated_at: asString(data.updated_at),
  };
};

/** Map one list item onto an {@link ArtStyleSummary}. Pure. */
export const parseArtStyleSummary = (body: unknown): ArtStyleSummary => {
  const data = asObject(body);
  return {
    id: asString(data.id),
    name: asString(data.name),
    status: data.status === 'active' ? 'active' : 'draft',
    thumb_url: asString(data.thumb_url),
    has_definition: data.has_definition === true,
    mood_count: asNumber(data.mood_count) ?? 0,
    mascot_count: asNumber(data.mascot_count) ?? 0,
    mature: data.mature === true,
    created_at: asString(data.created_at),
    updated_at: asString(data.updated_at),
  };
};

const parseArtStyleList = (body: unknown): ArtStyleSummary[] => {
  const items = asObject(body).items;
  return Array.isArray(items) ? items.map(parseArtStyleSummary).filter((item) => item.id !== '') : [];
};

/** A full record as the list would summarise it (the same `thumb_url` rule as the service), so a record that
 *  lands on the page updates its card without waiting for a re-list. `created_at` is not on the record. */
export const summaryOfArtStyle = (style: ArtStyle): Omit<ArtStyleSummary, 'created_at'> => {
  const newest = Object.values(style.samples)
    .flat()
    .reduce<ArtStyleSample | undefined>(
      (best, sample) => (!best || Date.parse(sample.created_at) > Date.parse(best.created_at) ? sample : best),
      undefined,
    );
  const preview = style.previews?.items.find((item) => item.state === 'ready' && item.candidate_url !== '');
  return {
    id: style.id,
    name: style.name,
    status: style.status,
    thumb_url:
      style.samples.hero[0]?.url || newest?.url || preview?.candidate_url || style.mood_images[0]?.url || '',
    has_definition: style.definition.trim() !== '',
    mood_count: style.mood_images.length,
    mascot_count: style.mascots.length,
    mature: style.mature,
    updated_at: style.updated_at,
  };
};

/** A sample draw's jobs: a slot's, or a preview batch follower's (`preview_<n>`); anything else is dropped. */
const isSamplePlacement = (slot: string): boolean =>
  ART_STYLE_SLOTS.some((s) => s === slot) || previewNumberOf(slot) !== undefined;

/** The 202 of a sample draw. Pure. */
export const parseSampleRun = (body: unknown): ArtStyleSampleRun => {
  const data = asObject(body);
  const jobs = Array.isArray(data.jobs)
    ? data.jobs
        .map((item) => asObject(item))
        .filter((item) => asString(item.job_id) !== '' && isSamplePlacement(asString(item.slot)))
        .map((item) => ({ slot: asString(item.slot), job_id: asString(item.job_id) }))
    : [];
  return { jobs, cost: asNumber(data.cost) ?? 0, batch_id: asString(data.batch_id) };
};

/** The 202 of an extract request. A body without a job id is not one (it reads as a failed call). Pure. */
export const parseExtractRun = (body: unknown): ArtStyleExtractRun => {
  const data = asObject(body);
  const jobId = asString(data.job_id);
  if (!jobId) throw new Error('The extraction returned no job');
  return { job_id: jobId, status: data.status === 'running' ? 'running' : 'queued' };
};

/** Map a status + body onto a result, given how to parse a 2xx body. Pure. */
export const artStyleResult = <T>(status: number, data: unknown, parse: (body: unknown) => T): ArtStyleResult<T> => {
  const body = asObject(data);
  const code = asString(body.error);
  if (status >= 200 && status < 300) return { ok: true, value: parse(data) };
  if (status === 404) return { ok: false, reason: 'unavailable', status };
  if (status === 402) {
    return {
      ok: false,
      reason: 'insufficient',
      status,
      needed: asNumber(body.needed),
      balance: asNumber(body.balance),
    };
  }
  if (status === 409 && code === 'stale_revision') return { ok: false, reason: 'stale', status, message: code };
  if ((status === 400 || status === 409) && code) return { ok: false, reason: 'refused', status, message: code };
  return { ok: false, reason: 'failed', status, message: code || undefined };
};

/* ── calls ──────────────────────────────────────────────────────────────────── */

const tenantBase = (tenantId: number): string =>
  `${creativesServiceBase()}/creatives/tenant/${encodeURIComponent(String(tenantId))}/art-styles`;

/** `…/art-styles/{style_id}{suffix}`. */
const stylePath = (tenantId: number, styleId: string, suffix = ''): string =>
  `${tenantBase(tenantId)}/${encodeURIComponent(styleId)}${suffix}`;

const UNAVAILABLE: ArtStyleFailure = { ok: false, reason: 'unavailable' };

/** Run one request, mapping every status (and a thrown network error) onto a result. */
const call = async <T>(
  send: () => Promise<{ status: number; data: unknown }>,
  parse: (body: unknown) => T,
): Promise<ArtStyleResult<T>> => {
  if (!creativesServiceConfigured()) return UNAVAILABLE;
  try {
    const response = await send();
    return artStyleResult(response.status, response.data, parse);
  } catch (error) {
    if (axios.isCancel(error)) return { ok: false, reason: 'failed', message: 'aborted' };
    return { ok: false, reason: 'failed' };
  }
};

/** Whether a per-style call failed because that style no longer exists (deleted, or another brand's id). The
 *  per-style routes only 404 for a missing style once the list route answered. */
export const isMissingStyle = (failure: ArtStyleFailure): boolean => failure.status === 404;

/** The brand's styles, newest-updated first. A 404 here means the service has no art-style routes. */
export const listArtStyles = (tenantId: number, signal?: AbortSignal): Promise<ArtStyleResult<ArtStyleSummary[]>> =>
  call(() => axios.get(tenantBase(tenantId), creativesRequestConfig(signal)), parseArtStyleList);

/** A new blank draft, or a duplicate of `fromStyleId` (its inputs and definition, not its samples). */
export const createArtStyle = (
  tenantId: number,
  fromStyleId?: string,
  signal?: AbortSignal,
): Promise<ArtStyleResult<ArtStyle>> =>
  call(
    () =>
      axios.post(tenantBase(tenantId), fromStyleId ? { from_style_id: fromStyleId } : {}, creativesRequestConfig(signal)),
    parseArtStyle,
  );

/** One style (404 when it no longer exists). */
export const fetchArtStyle = (
  tenantId: number,
  styleId: string,
  signal?: AbortSignal,
): Promise<ArtStyleResult<ArtStyle>> =>
  call(() => axios.get(stylePath(tenantId, styleId), creativesRequestConfig(signal)), parseArtStyle);

/** An image as a PUT sends it: `{url, name}` (the read-only `used_url` stays on the service's side). */
const imageBody = ({ url, name }: ArtStyleImage): ArtStyleImage => ({ url, name });

/** The PUT body of a patch: a cast goes as `{id, name, images}` per mascot (the service keeps each brief by id,
 *  and drops it when that mascot's images changed); images go as `{url, name}`. Pure. */
export const artStylePatchBody = ({ mascots, mood_images, ...rest }: ArtStylePatch): Json => ({
  ...rest,
  ...(mood_images ? { mood_images: mood_images.map(imageBody) } : {}),
  ...(mascots
    ? { mascots: mascots.map(({ id, name, images }) => ({ id, name, images: images.map(imageBody) })) }
    : {}),
});

/** Partial update of the owner's inputs. `expectedRevision` guards against a lost update (409 `stale`). */
export const saveArtStyle = (
  tenantId: number,
  styleId: string,
  patch: ArtStylePatch,
  expectedRevision: number | undefined,
  signal?: AbortSignal,
): Promise<ArtStyleResult<ArtStyle>> =>
  call(
    () =>
      axios.put(
        stylePath(tenantId, styleId),
        {
          ...artStylePatchBody(patch),
          ...(expectedRevision !== undefined ? { expected_revision: expectedRevision } : {}),
        },
        creativesRequestConfig(signal),
      ),
    parseArtStyle,
  );

/** Delete a style for good (204). Deleting the active one leaves the brand with no active style. */
export const deleteArtStyle = (tenantId: number, styleId: string, signal?: AbortSignal): Promise<ArtStyleResult<true>> =>
  call(() => axios.delete(stylePath(tenantId, styleId), creativesRequestConfig(signal)), () => true as const);

/** Store one reference image (base64 with no `data:` prefix). Returns its URL; it is NOT added to the record,
 *  the caller saves the new list. */
export const uploadArtStyleImage = (
  tenantId: number,
  styleId: string,
  input: { data: string; filename: string; kind: ArtStyleImageKind },
  signal?: AbortSignal,
): Promise<ArtStyleResult<{ url: string; mime_type: string }>> =>
  call(
    () => axios.post(stylePath(tenantId, styleId, '/upload'), input, creativesRequestConfig(signal)),
    (body) => {
      const data = asObject(body);
      const url = asString(data.url);
      if (!url) throw new Error('The upload returned no url');
      return { url, mime_type: asString(data.mime_type) };
    },
  );

/** Queue the extraction of a style definition from the saved inputs (free; 202 with the job). It runs as an
 *  `art_style_extract` creative job, so it outlives the page: its progress arrives with the shared jobs feed, and
 *  once it is done the record holds the definition and the style's four sample draws are queued (each charged).
 *  A style already being extracted answers that job. The first extraction makes the style active only when the
 *  brand has no active style. */
export const extractArtStyle = (
  tenantId: number,
  styleId: string,
  signal?: AbortSignal,
): Promise<ArtStyleResult<ArtStyleExtractRun>> =>
  call(() => axios.post(stylePath(tenantId, styleId, '/extract'), {}, creativesRequestConfig(signal)), parseExtractRun);

/** Swap the definition with the one before it (doubles as re-apply). */
export const undoArtStyle = (tenantId: number, styleId: string, signal?: AbortSignal): Promise<ArtStyleResult<ArtStyle>> =>
  call(() => axios.post(stylePath(tenantId, styleId, '/undo'), {}, creativesRequestConfig(signal)), parseArtStyle);

/** Have Lumio look at one mascot's SAVED images (synchronous, free): it writes the mascot's brief and, when its
 *  name is empty, a short name for it. Returns the whole record. */
export const describeArtStyleMascot = (
  tenantId: number,
  styleId: string,
  mascotId: string,
  signal?: AbortSignal,
): Promise<ArtStyleResult<ArtStyle>> =>
  call(
    () =>
      axios.post(
        stylePath(tenantId, styleId, `/mascots/${encodeURIComponent(mascotId)}/describe`),
        {},
        creativesRequestConfig(signal),
      ),
    parseArtStyle,
  );

/** Draw samples (all four slots by default). Charged up front; 402 when the balance can't cover it. On the
 *  casino_creation pipeline `slots` is ignored: every request is one preview batch of four ("Regenerate samples"),
 *  charged four samples, refused with 409 `art_style_previews_running` while the newest batch still draws. */
export const drawArtStyleSamples = (
  tenantId: number,
  styleId: string,
  slots?: readonly ArtStyleSlot[],
  signal?: AbortSignal,
): Promise<ArtStyleResult<ArtStyleSampleRun>> =>
  call(
    () =>
      axios.post(stylePath(tenantId, styleId, '/samples'), slots ? { slots } : {}, creativesRequestConfig(signal)),
    parseSampleRun,
  );

/** Use the style for the brand's generation (`active: true`, needs a definition; pauses the brand's other
 *  active style) or pause it. */
export const activateArtStyle = (
  tenantId: number,
  styleId: string,
  active: boolean,
  signal?: AbortSignal,
): Promise<ArtStyleResult<ArtStyle>> =>
  call(
    () => axios.post(stylePath(tenantId, styleId, '/activate'), { active }, creativesRequestConfig(signal)),
    parseArtStyle,
  );

/* ── the brand's website look (site jobs) ───────────────────────────────────── */

/**
 * The brand's website look in its own art style and its latest site job (`GET …/site-regen`):
 * - `idle`: the brand never ran a site job;
 * - `drawing`: "Regenerate website art" runs (casino-creation draws the art in the brand's own style, then switches
 *   the site to it: it ends `live`);
 * - `ready`: the art is drawn and previewable, the site does not show it yet (a regeneration from before it switched
 *   the site);
 * - `switching` then `live`: "Switch my site" runs, then the site shows the brand's own style;
 * - `reverting` then `original`: "Back to the original look" runs, then the site shows its original style again;
 * - `failed`: the latest job failed (`error`, `failed_parts`, `error_message`).
 */
export type SiteRegenPhase = 'idle' | 'drawing' | 'ready' | 'switching' | 'live' | 'reverting' | 'original' | 'failed';

const SITE_REGEN_PHASES: readonly SiteRegenPhase[] = [
  'idle',
  'drawing',
  'ready',
  'switching',
  'live',
  'reverting',
  'original',
  'failed',
];

/** The latest site job's action ("" when idle): the brand's own look (regenerate, switch, revert) or one of the brand's
 *  catalog looks (`look_draw`: its art is drawn next to the current look, phase drawing then ready; `look_apply`: the
 *  site switches to it, phase switching then live). A look action names its look in `target_style_id`. */
export type SiteRegenAction = 'regenerate' | 'switch' | 'revert' | 'look_draw' | 'look_apply' | '';

const SITE_REGEN_ACTIONS: readonly SiteRegenAction[] = [
  'regenerate',
  'switch',
  'revert',
  'look_draw',
  'look_apply',
  '',
];

/** The step inside a running phase: syncing the style, asking casino-creation, waiting for it to be free (it is
 *  busy with the brand's other work), following its pipeline; "" otherwise. */
export type SiteRegenActivity = 'preparing' | 'submitting' | 'waiting' | 'following' | '';

const SITE_REGEN_ACTIVITIES: readonly SiteRegenActivity[] = ['preparing', 'submitting', 'waiting', 'following', ''];

/** The job kind of every site job (regenerate, switch, revert); its `creative_job` events carry `stage` = the phase,
 *  `activity`, `completed` / `total` parts. */
export const SITE_REGEN_KIND = 'site_regen';

/**
 * The price-table key of a full-site art redraw (spark-owned, `GET /credits/pricing`): what "Regenerate website art"
 * (also reached through "Set as primary" for a style the site does not show) and a catalog look's "Generate art in
 * this style" cost. Charged once, when the job is queued (402 when the balance can't cover it), and refunded only when
 * casino-creation never took the work or cancelled it; a Retry that resumes a failed job is free. Switching, reverting
 * and a catalog look's "Set as primary" draw nothing and are never charged. The same string as
 * {@link SITE_REGEN_KIND}, but a different table: one names the job, this one its price.
 */
export const SITE_REGEN_PRICE_KEY = 'site_regen';

/** `GET …/site-regen` (and the 202 of each site action). Every key is always present. */
export interface SiteRegenState {
  phase: SiteRegenPhase;
  action: SiteRegenAction;
  activity: SiteRegenActivity;
  job_id: string;
  pipeline_id: string;
  steps_done: number;
  /** 0 while unknown. */
  steps_total: number;
  /** Website targets (`welcome_bonus`, `above_the_fold_hero`, `evergreen:<NAME>`, `gamification:<CLASS>:<TIER>`),
   *  `system_art`, `switch`, or (for `site_regen_missing_art`) the ids of the deployments casino-creation named. */
  failed_parts: string[];
  /** The bare code a failed job ended with; "" otherwise. */
  error: string;
  /** casino-creation's own reason when the switch or the shared art failed (one line); "" otherwise. */
  error_message: string;
  cc_style_id: string;
  /** The spark style the latest regeneration drew (or the brand's style was last synced from). */
  spark_style_id: string;
  /** The brand's active style is newer than the one last sent to its website (another style, or edited since). */
  spark_style_outdated: boolean;
  /** The style "Back to the original look" applies; "" when none is on record. */
  original_style_id: string;
  /** The query that shows the site in the brand's own style before it is switched; "" before any regeneration. */
  preview_param: string;
  /** The look the latest job targets (a catalog look's id, or the brand's own look); "" when idle or not known. */
  target_style_id: string;
  /** What the website shows now: a catalog look's id, the brand's own look (`owner-<tenant>`), or "" when mixed or not
   *  known (a service from before the catalog looks sends none). */
  live_style_id: string;
  /** The brand's own look has art (its newest regeneration completed), so "Switch my site" can show it. */
  owner_drawn: boolean;
  started_at: string;
  finished_at: string;
  updated_at: string;
}

/** Map a site-regen body onto a {@link SiteRegenState}. Pure; a phase it does not know reads as idle. */
export const parseSiteRegenState = (body: unknown): SiteRegenState => {
  const data = asObject(body);
  return {
    phase: oneOf(SITE_REGEN_PHASES, data.phase, 'idle'),
    action: oneOf(SITE_REGEN_ACTIONS, data.action, ''),
    activity: oneOf(SITE_REGEN_ACTIVITIES, data.activity, ''),
    job_id: asString(data.job_id),
    pipeline_id: asString(data.pipeline_id),
    steps_done: asNumber(data.steps_done) ?? 0,
    steps_total: asNumber(data.steps_total) ?? 0,
    failed_parts: Array.isArray(data.failed_parts)
      ? data.failed_parts.filter((part): part is string => typeof part === 'string' && part !== '')
      : [],
    error: asString(data.error),
    error_message: asString(data.error_message),
    cc_style_id: asString(data.cc_style_id),
    spark_style_id: asString(data.spark_style_id),
    spark_style_outdated: data.spark_style_outdated === true,
    original_style_id: asString(data.original_style_id),
    preview_param: asString(data.preview_param),
    target_style_id: asString(data.target_style_id),
    live_style_id: asString(data.live_style_id),
    owner_drawn: data.owner_drawn === true,
    started_at: asString(data.started_at),
    finished_at: asString(data.finished_at),
    updated_at: asString(data.updated_at),
  };
};

const siteRegenPath = (tenantId: number, suffix = ''): string =>
  `${creativesServiceBase()}/creatives/tenant/${encodeURIComponent(String(tenantId))}/site-regen${suffix}`;

/** The brand's website look (phase idle when it never ran a site job). A 404 means the route is not deployed. */
export const fetchSiteRegen = (tenantId: number, signal?: AbortSignal): Promise<ArtStyleResult<SiteRegenState>> =>
  call(() => axios.get(siteRegenPath(tenantId), creativesRequestConfig(signal)), parseSiteRegenState);

/** "Regenerate website art" in the brand's ACTIVE style (charged {@link SITE_REGEN_PRICE_KEY} when queued): syncs the
 *  saved style to the website, draws its art, then switches the site to it (casino-creation's restyle with apply). 202
 *  with the queued state (phase drawing); 402 when the balance can't cover it; 409 with a code when it cannot start. */
export const regenerateSiteArt = (
  tenantId: number,
  styleId: string,
  signal?: AbortSignal,
): Promise<ArtStyleResult<SiteRegenState>> =>
  call(
    () => axios.post(stylePath(tenantId, styleId, '/regenerate-site'), {}, creativesRequestConfig(signal)),
    parseSiteRegenState,
  );

/** "Switch my site": the site shows the brand's own style once the job is done. 202 (phase switching). */
export const switchSiteArt = (tenantId: number, signal?: AbortSignal): Promise<ArtStyleResult<SiteRegenState>> =>
  call(() => axios.post(siteRegenPath(tenantId, '/apply'), {}, creativesRequestConfig(signal)), parseSiteRegenState);

/** "Back to the original look": the site shows its original style again once the job is done. 202 (reverting). */
export const revertSiteArt = (tenantId: number, signal?: AbortSignal): Promise<ArtStyleResult<SiteRegenState>> =>
  call(() => axios.post(siteRegenPath(tenantId, '/revert'), {}, creativesRequestConfig(signal)), parseSiteRegenState);

/** The refusal of a resume when the brand's latest site job left no failed pipeline to retry. */
export const NOTHING_TO_RESUME = 'site_regen_nothing_to_resume';

/** "Retry" of the brand's failed site job where it stopped: casino-creation retries its pipeline in place, so only the
 *  parts it did not finish run again. Refused {@link NOTHING_TO_RESUME} when there is no failed pipeline to retry. */
export const resumeSiteArt = (tenantId: number, signal?: AbortSignal): Promise<ArtStyleResult<SiteRegenState>> =>
  call(() => axios.post(siteRegenPath(tenantId, '/resume'), {}, creativesRequestConfig(signal)), parseSiteRegenState);

/* ── the brand's catalog looks (site looks) ─────────────────────────────────── */

/** How much of the brand's website art exists in a look: all of it, some of it, or none. */
export type SiteLookArt = 'ready' | 'partial' | 'none';

const SITE_LOOK_ARTS: readonly SiteLookArt[] = ['ready', 'partial', 'none'];

/**
 * One catalog look the brand's website can show (`GET …/site-looks`): a casino-creation banner set compatible with the
 * brand's design. Catalog looks are read-only: their art can be drawn for the brand and the site switched to them.
 */
export interface SiteLook {
  /** The look's id (casino-creation's banner set id). */
  style_id: string;
  /** The owner-hub catalog entry it stands for (0 when not known). */
  art_style_id: number;
  /** The catalog name, as the catalog writes it. */
  name: string;
  thumbnail_url: string;
  /** The brand's own art in this look when it has some, else the catalog thumbnail ("" when neither). */
  cover_url: string;
  /** A screenshot of a site of the brand's design in this look ("" when none). */
  screenshot_url: string;
  mature: boolean;
  /** The look the brand's site launched with. */
  original: boolean;
  /** The website shows it now. */
  live: boolean;
  art: SiteLookArt;
}

/** `GET …/site-looks`. */
export interface SiteLooks {
  /** The original look first, then the catalog's order. */
  looks: SiteLook[];
  /** What the website shows now (a look's id, the brand's own look, or "" when mixed or not known). */
  live_style_id: string;
  original_style_id: string;
  /** The brand's own look's id (`owner-<tenant>`). */
  owner_style_id: string;
}

/** One piece of the brand's own art in a look: one format of one of its banners. */
export interface LookSample {
  deployment_uuid: string;
  /** The banner's name ("Welcome Bonus"). */
  name: string;
  /** The format slot (`ar21_keyright`). */
  slot: string;
  /** Width over height (0 when not known). */
  aspect: number;
  url: string;
}

/** Whether an id is a brand's own look rather than a catalog one. Pure. */
export const isOwnerLookId = (styleId: string): boolean => styleId.startsWith('owner-');

/** Map a site-looks body onto {@link SiteLooks}: looks without an id, owner looks and repeats are dropped. Pure. */
export const parseSiteLooks = (body: unknown): SiteLooks => {
  const data = asObject(body);
  const seen = new Set<string>();
  const looks: SiteLook[] = [];
  for (const raw of Array.isArray(data.looks) ? data.looks : []) {
    const item = asObject(raw);
    const styleId = asString(item.style_id);
    if (!styleId || isOwnerLookId(styleId) || seen.has(styleId)) continue;
    seen.add(styleId);
    const thumbnail = asString(item.thumbnail_url);
    looks.push({
      style_id: styleId,
      art_style_id: asNumber(item.art_style_id) ?? 0,
      name: asString(item.name),
      thumbnail_url: thumbnail,
      cover_url: asString(item.cover_url) || thumbnail,
      screenshot_url: asString(item.screenshot_url),
      mature: item.mature === true,
      original: item.original === true,
      live: item.live === true,
      art: oneOf(SITE_LOOK_ARTS, item.art, 'none'),
    });
  }
  return {
    looks,
    live_style_id: asString(data.live_style_id),
    original_style_id: asString(data.original_style_id),
    owner_style_id: asString(data.owner_style_id),
  };
};

/** Map a look-samples body onto its samples: the slot as Website Creatives keys it, the aspect from the body, else from
 *  the slot. Samples without a url are dropped. Pure. */
export const parseLookSamples = (body: unknown): LookSample[] => {
  const items = asObject(body).samples;
  if (!Array.isArray(items)) return [];
  return items
    .map((raw) => asObject(raw))
    .filter((item) => asString(item.url) !== '')
    .map((item) => {
      const rawSlot = asString(item.slot);
      const slot = slotOfVariant(rawSlot) || rawSlot;
      const aspect = asNumber(item.aspect);
      return {
        deployment_uuid: asString(item.deployment_uuid),
        name: asString(item.name),
        slot,
        aspect: aspect !== undefined && aspect > 0 ? aspect : slotAspect(slot) ?? 0,
        url: asString(item.url),
      };
    });
};

const siteLooksPath = (tenantId: number, suffix = ''): string =>
  `${creativesServiceBase()}/creatives/tenant/${encodeURIComponent(String(tenantId))}/site-looks${suffix}`;

const lookPath = (tenantId: number, styleId: string, suffix = ''): string =>
  siteLooksPath(tenantId, `/${encodeURIComponent(styleId)}${suffix}`);

/** The brand's catalog looks. A 404 means the route is not deployed (the page then works as before them). */
export const fetchSiteLooks = (tenantId: number, signal?: AbortSignal): Promise<ArtStyleResult<SiteLooks>> =>
  call(() => axios.get(siteLooksPath(tenantId), creativesRequestConfig(signal)), parseSiteLooks);

/** The brand's own art in one look (empty when it has none yet). 404 `site_look_unknown` for a look not the brand's. */
export const fetchLookSamples = (
  tenantId: number,
  styleId: string,
  signal?: AbortSignal,
): Promise<ArtStyleResult<LookSample[]>> =>
  call(() => axios.get(lookPath(tenantId, styleId, '/samples'), creativesRequestConfig(signal)), parseLookSamples);

/** "Generate art in this style": draws the brand's website art in a catalog look next to its current look, without
 *  changing what players see (charged {@link SITE_REGEN_PRICE_KEY} when queued). 202 with the queued state (action
 *  look_draw, phase drawing); 402 when the balance can't cover it. */
export const drawLook = (
  tenantId: number,
  styleId: string,
  signal?: AbortSignal,
): Promise<ArtStyleResult<SiteRegenState>> =>
  call(() => axios.post(lookPath(tenantId, styleId, '/draw'), {}, creativesRequestConfig(signal)), parseSiteRegenState);

/** "Set as primary" for a catalog look: switches the website to it (its art must be drawn). 202 with the queued state
 *  (action look_apply, phase switching). */
export const applyLook = (
  tenantId: number,
  styleId: string,
  signal?: AbortSignal,
): Promise<ArtStyleResult<SiteRegenState>> =>
  call(
    () => axios.post(lookPath(tenantId, styleId, '/apply'), {}, creativesRequestConfig(signal)),
    parseSiteRegenState,
  );
